import React from "react";
import { Trash } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Location } from "./CampaignFormSchema";

interface LocationSelectorProps {
  locations: Location[];
  setLocations: React.Dispatch<React.SetStateAction<Location[]>>;
  countries: string[];
  states: string[];
  districts: { [locationIndex: number]: string[] };
  isLoadingCountries: boolean;
  isLoadingStates: boolean;
  isLoadingDistricts: boolean;
  onLocationChange: (index: number, field: keyof Location, value: string | number) => void;
  onAddLocation: () => void;
  onRemoveLocation: (index: number) => void;
}

export function LocationSelector({
  locations,
  setLocations,
  countries,
  states,
  districts,
  isLoadingCountries,
  isLoadingStates,
  isLoadingDistricts,
  onLocationChange,
  onAddLocation,
  onRemoveLocation,
}: LocationSelectorProps) {
  // Prevent scroll wheel from changing number input values
  const preventWheelChange = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
    e.preventDefault();
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Campaign Locations</h3>
      <p className="text-sm text-muted-foreground">
        Specify the locations where this campaign will take place
      </p>
      
      {locations.map((location, index) => (
        <Card key={index} className="overflow-hidden">
          <CardHeader className="p-4 pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Location {index + 1}</CardTitle>
            {locations.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRemoveLocation(index)}
              >
                <Trash className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`location-country-${index}`}>Country</Label>
                <Select
                  value={location.country}
                  onValueChange={(value) => onLocationChange(index, 'country', value)}
                >
                  <SelectTrigger id={`location-country-${index}`}>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingCountries ? (
                      <div className="flex justify-center p-2">
                        <Skeleton className="h-4 w-20" />
                      </div>
                    ) : countries.length > 0 ? (
                      countries.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="_no_countries" disabled>
                        No countries available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`location-state-${index}`}>State</Label>
                <Select
                  value={location.state}
                  onValueChange={(value) => onLocationChange(index, 'state', value)}
                  disabled={!location.country || isLoadingStates}
                >
                  <SelectTrigger id={`location-state-${index}`}>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingStates ? (
                      <div className="flex justify-center p-2">
                        <Skeleton className="h-4 w-20" />
                      </div>
                    ) : states.length > 0 ? (
                      states.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="_no_states" disabled>
                        {location.country ? "No states available" : "Select a country first"}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`location-district-${index}`}>City</Label>
                <Select
                  value={location.district}
                  onValueChange={(value) => onLocationChange(index, 'district', value)}
                  disabled={!location.state || isLoadingDistricts}
                >
                  <SelectTrigger id={`location-district-${index}`}>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingDistricts ? (
                      <div className="flex justify-center p-2">
                        <Skeleton className="h-4 w-20" />
                      </div>
                    ) : districts[index]?.length > 0 ? (
                      districts[index].map((district) => (
                        <SelectItem key={`${district}-${index}`} value={district}>
                          {district}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="_no_districts" disabled>
                        {location.state ? "No districts available" : "Select a state first"}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`location-volunteers-${index}`}>Number of Volunteers</Label>
                <Input
                  id={`location-volunteers-${index}`}
                  type="number"
                  min="1"
                  value={location.volunteers}
                  onChange={(e) => onLocationChange(index, 'volunteers', parseInt(e.target.value) || 1)}
                  onWheel={preventWheelChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`location-details-${index}`}>Additional Details</Label>
                <Input
                  id={`location-details-${index}`}
                  placeholder="Address, venue, or other details"
                  value={location.details}
                  onChange={(e) => onLocationChange(index, 'details', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      
      <Button
        type="button"
        variant="outline"
        onClick={onAddLocation}
        className="w-full"
      >
        Add Another Location
      </Button>
    </div>
  );
}
